import React, { useState } from 'react';
import { useJarvisContext, A } from '../../context/JarvisContext';
import WifiConnect from '../Shared/WifiConnect';

// All screens
import JarvisHome        from './JarvisHome';
import BottomBar         from './BottomBar';
import ChatScreen        from '../Chat/ChatScreen';
import Dashboard         from '../Dashboard/Dashboard';
import ICTModule         from '../ICT/ICTModule';
import FibCalculator     from '../Fibonacci/FibCalculator';
import ConfluenceScorer  from '../Confluence/ConfluenceScorer';
import TradeJournal      from '../Journal/TradeJournal';
import RiskManager       from '../RiskManager/RiskManager';
import DailyTeaching     from '../Daily/DailyTeaching';
import KnowledgeBase     from '../Knowledge/KnowledgeBase';
import MultiAgentScreen  from '../MultiAgent/MultiAgentScreen';
import StatsScreen       from '../Stats/StatsScreen';
import PsychologyTracker from '../Psychology/PsychologyTracker';
import JarvisMemory      from '../Memory/JarvisMemory';
import SettingsScreen    from '../Settings/SettingsScreen';
import AgentNetwork      from '../AgentNetwork/AgentNetwork';
import ClaudeBridge      from '../Daily/ClaudeBridge';
import IndiaOverview     from '../India/IndiaOverview';
import StockAnalyzer     from '../India/StockAnalyzer';
import StockScreener     from '../India/StockScreener';
import IndiaJournal      from '../India/IndiaJournal';
import ChartVision       from '../Learn/ChartVision';
import EconomicCalendar  from '../Learn/EconomicCalendar';
import LearningImport    from '../Learn/LearningImport';
import TradeImport       from '../Learn/TradeImport';
import BrokerConnect     from '../Learn/BrokerConnect';
import RuleBook          from '../Rules/RuleBook';
import UserGuide         from '../Guide/UserGuide';
import LiveMonitor       from '../ScreenMonitor/LiveMonitor';

const STYLES = `
@keyframes sh1    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
@keyframes sh2    { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
@keyframes shP    { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(1.1)} }
@keyframes shG    { 0%,100%{box-shadow:0 0 8px #00d4ff44} 50%{box-shadow:0 0 20px #00d4ff88,0 0 40px #00d4ff33} }
@keyframes navIco { 0%,100%{filter:drop-shadow(0 0 2px #00d4ff)} 50%{filter:drop-shadow(0 0 7px #00d4ff)} }
@keyframes dotB   { 0%,100%{opacity:1} 50%{opacity:0.15} }

/* ═══ V5 GLOBAL SCREEN FIXES ═══════════════════════════════════════════ */

/* 1. Force all screens to fill full width — overrides any inline maxWidth */
.v5-content,
.v5-content > div { width: 100% !important; max-width: none !important; box-sizing: border-box; }

/* 2. Fix very narrow screens (480px, 800px etc) — remove their centering.
      NOTE on the "space after the colon" question (got this backwards
      once already, corrected here for good): a same-day change briefly
      "fixed" these selectors to "margin:0px auto" (no space) based on
      react-dom/server's renderToStaticMarkup output — but this app is
      pure client-side-rendered (ReactDOM.createRoot().render()), which
      never goes through that code path at all. Checked the ACTUAL live
      DOM instead (Playwright, element.getAttribute('style') /
      element.matches(...) on the real deployed site): the browser's own
      CSSOM re-serializes the style attribute WITH a space —
      "display: flex", "margin: 0px auto" — every time, confirmed via
      element.matches('[style*="display: grid"]') === true and the
      no-space form === false. Server-string serialization and live-DOM
      attribute serialization are just different formats; only the
      second one is what a CSS attribute selector ever matches against. */
.v5-content [style*="margin: 0px auto"],
.v5-content [style*="margin: 0 auto"] {
  margin-left: 0 !important;
  margin-right: 0 !important;
  width: 100% !important;
  max-width: none !important;
}

/* 3. Fix input / select / textarea styling */
.v5-content select,
.v5-content input:not([type="checkbox"]):not([type="radio"]):not([type="range"]),
.v5-content textarea {
  background: rgba(255,255,255,0.06) !important;
  border: 1px solid rgba(255,255,255,0.15) !important;
  color: #e2e8f0 !important;
  border-radius: 8px !important;
  padding: 8px 12px !important;
  font-size: 14px !important;
  outline: none !important;
  box-sizing: border-box;
}
.v5-content select option {
  background: #0d1121 !important;
  color: #e2e8f0 !important;
}

/* 4. Fix tabs/pills with very low contrast text */
.v5-content button,
.v5-content [role="tab"],
.v5-content [role="button"] {
  font-family: inherit !important;
}

/* 5. Fix any element using color #64748b (too dark on dark bg) */
.v5-content [style*="color: rgb(100, 116, 139)"],
.v5-content [style*="color: #64748b"] {
  color: #94a3b8 !important;
}

/* 6. Range/slider styling */
.v5-content input[type="range"] {
  accent-color: #00d4ff;
  width: 100%;
}

/* 7. Tables */
.v5-content table { width: 100% !important; border-collapse: collapse; }
.v5-content th, .v5-content td {
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  color: #e2e8f0;
  font-size: 13px;
  text-align: left;
}
.v5-content th { color: rgba(255,255,255,0.4); font-size: 11px; letter-spacing: 1px; text-transform: uppercase; }

/* 8. Make all text bigger — bump every size up by ~2px */
.v5-content [style*="font-size: 10px"] { font-size: 12px !important; }
.v5-content [style*="font-size: 11px"] { font-size: 13px !important; }
.v5-content [style*="font-size: 12px"] { font-size: 14px !important; }
.v5-content [style*="font-size: 13px"] { font-size: 15px !important; }
.v5-content [style*="font-size: 14px"] { font-size: 16px !important; }
.v5-content [style*="font-size: 15px"] { font-size: 17px !important; }

/* 9. Scrollbars */
.v5-content::-webkit-scrollbar, .v5-content *::-webkit-scrollbar { width: 4px; height: 4px; }
.v5-content::-webkit-scrollbar-track, .v5-content *::-webkit-scrollbar-track { background: transparent; }
.v5-content::-webkit-scrollbar-thumb, .v5-content *::-webkit-scrollbar-thumb { background: rgba(0,212,255,0.3); border-radius: 4px; }

/* ═══ MOBILE LAYOUT (<=860px) ══════════════════════════════════════════
   The whole shell below was built desktop-only: fixed 252px sidebar
   permanently in the flex row, no breakpoints anywhere. On a phone that
   put the nav list and the screen content side-by-side in ~180px each,
   which is what was rendering as the overlapping/unreadable mess. Fix:
   the sidebar becomes an off-canvas drawer (hidden by default, toggled
   by a hamburger button that only exists at this width), and every
   screen's own hardcoded multi-column grid/no-wrap flex row is forced to
   reflow — this is a global safety net for the ~25 screens this pass
   doesn't hand-edit one by one, on top of the two (V5Shell, JarvisHome)
   that got proper dedicated fixes below. */
@media (max-width: 860px) {
  .v5-sidebar {
    position: fixed !important;
    /* Starts below the header (not top:0) so the header — and its ✕
       close toggle — stays visible/tappable above the open drawer,
       instead of the drawer's z-index covering it. */
    top: 66px; bottom: 0; left: 0;
    width: 82vw !important;
    max-width: 300px;
    height: auto !important;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    z-index: 2000;
    box-shadow: 12px 0 32px rgba(0,0,0,0.55);
  }
  .v5-sidebar.open { transform: translateX(0); }
  .v5-hamburger { display: flex !important; }
  .v5-header-title { display: none !important; }
  .v5-wifi-btn { display: none !important; }
  .v5-header-icons { margin-left: auto !important; }

  /* Any hardcoded multi-column grid on any screen collapses to however
     many ~140px columns actually fit (usually 1, sometimes 2) instead
     of squeezing 2-3 fixed columns into a too-narrow width. "display:
     grid" (space after colon) — see the long note on rule 2 above for
     why; confirmed against the live DOM, not assumed. */
  .v5-content [style*="display: grid"] {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)) !important;
  }
  /* Any non-wrapping flex row wraps instead of squashing its children —
     only kicks in when content actually doesn't fit, so rows that
     already fit are untouched. */
  .v5-content [style*="display: flex"] {
    flex-wrap: wrap !important;
  }
  .v5-content { padding-left: 2px !important; padding-right: 2px !important; }
}
.v5-overlay {
  display: none;
  position: fixed; top: 66px; left: 0; right: 0; bottom: 0; z-index: 1900;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(2px);
}
@media (max-width: 860px) {
  .v5-overlay.open { display: block; }
}
`;

// Mobile breakpoint match — kept purely for the hamburger's initial
// open/closed default so a phone doesn't briefly flash a full sidebar
// before CSS applies. All actual show/hide logic below is CSS-driven
// (see @media (max-width: 860px) above) so it works even before this
// state settles, and stays correct across resizes without needing to
// duplicate the breakpoint number in JS.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 860
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    setIsMobile(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

// ── Animated header HUD
function HeaderHud({ size = 52 }) {
  const s = size;
  const r = { position:'absolute', borderRadius:'50%' };
  return (
    <div style={{ position:'relative', width:s, height:s, flexShrink:0 }}>
      <div style={{ ...r, inset:0, border:'1.5px solid rgba(0,212,255,0.15)' }}/>
      <div style={{ ...r, inset:s*0.1, border:'1.5px solid rgba(0,212,255,0.3)',
        animation:'sh1 8s linear infinite' }}>
        <div style={{ position:'absolute', top:'-3px', left:'50%', width:'6px', height:'6px',
          background:'#00d4ff', borderRadius:'50%', transform:'translateX(-50%)',
          boxShadow:'0 0 6px #00d4ff' }}/>
      </div>
      <div style={{ ...r, inset:s*0.24, border:'1px dashed rgba(0,212,255,0.4)',
        animation:'sh2 5s linear infinite' }}/>
      <div style={{ ...r, inset:s*0.38, border:'2px solid rgba(0,212,255,0.5)',
        animation:'sh1 12s linear infinite' }}/>
      <div style={{ ...r, inset:s*0.38,
        background:'radial-gradient(circle, #00d4ff 0%, #0044cc 60%, #001144 100%)',
        animation:'shP 2.5s ease-in-out infinite',
        boxShadow:'0 0 14px #00d4ff, 0 0 28px #00d4ff44' }}/>
    </div>
  );
}

// ── Sidebar nav — with emojis + card style per item
const NAV_GROUPS = [
  {
    group: 'MAIN',
    items: [
      { id:'v5home',    emoji:'🏠', label:'Home'             },
      { id:'v5chat',    emoji:'💬', label:'Chat with JARVIS'  },
      { id:'dashboard', emoji:'🖥️', label:'Dashboard'        },
    ],
  },
  {
    group: 'TRADING',
    items: [
      { id:'journal',     emoji:'📓', label:'Trade Journal'   },
      { id:'risk',        emoji:'🛡️', label:'Risk Manager'    },
      { id:'stats',       emoji:'📈', label:'Statistics'      },
      { id:'psychology',  emoji:'🧘', label:'Psychology'      },
    ],
  },
  {
    group: 'ANALYSIS',
    items: [
      { id:'ict',         emoji:'🎯', label:'ICT / SMC'       },
      { id:'fibonacci',   emoji:'📐', label:'Fibonacci'       },
      { id:'confluence',  emoji:'⚡', label:'Confluence'      },
      { id:'livemonitor', emoji:'📡', label:'Live Monitor'    },
      { id:'agentnetwork',emoji:'🤖', label:'Agent Network'   },
      { id:'multiagent',  emoji:'👥', label:'Multi-Agent AI'  },
    ],
  },
  {
    group: 'LEARN',
    items: [
      { id:'daily',         emoji:'📚', label:'Daily Teaching'  },
      { id:'knowledge',     emoji:'🧠', label:'Knowledge Base'  },
      { id:'rules',         emoji:'📋', label:'Rule Book'       },
      { id:'chartvision',   emoji:'👁️', label:'Chart Vision AI' },
      { id:'calendar',      emoji:'📅', label:'Economic Calendar'},
      { id:'learningimport',emoji:'▶️', label:'YouTube / PDF'   },
      { id:'tradeimport',   emoji:'📊', label:'MT4/MT5 Import'  },
      { id:'brokerconnect', emoji:'🔗', label:'Broker Connect'  },
    ],
  },
  {
    group: 'INDIA',
    items: [
      { id:'india',         emoji:'🇮🇳', label:'Market Overview' },
      { id:'analyzer',      emoji:'🔍', label:'Stock Analyzer'  },
      { id:'screener',      emoji:'📉', label:'Screener + Picks'},
      { id:'indiajournal',  emoji:'📒', label:'India Journal'   },
    ],
  },
  {
    group: 'SYSTEM',
    items: [
      { id:'guide',    emoji:'📖', label:'User Guide'      },
      { id:'memory',   emoji:'💾', label:'JARVIS Memory'  },
      { id:'bridge',   emoji:'🌉', label:'Claude Bridge'   },
      { id:'settings', emoji:'⚙️', label:'Settings'       },
    ],
  },
];

// ── Screen router
function V5Screen({ id }) {
  switch (id) {
    case 'v5home':         return <JarvisHome />;
    case 'v5chat':         return <ChatScreen />;
    case 'dashboard':      return <Dashboard />;
    case 'ict':            return <ICTModule />;
    case 'fibonacci':      return <FibCalculator />;
    case 'confluence':     return <ConfluenceScorer />;
    case 'livemonitor':    return <LiveMonitor />;
    case 'journal':        return <TradeJournal />;
    case 'risk':           return <RiskManager />;
    case 'daily':          return <DailyTeaching />;
    case 'knowledge':      return <KnowledgeBase />;
    case 'multiagent':     return <MultiAgentScreen />;
    case 'stats':          return <StatsScreen />;
    case 'psychology':     return <PsychologyTracker />;
    case 'memory':         return <JarvisMemory />;
    case 'settings':       return <SettingsScreen />;
    case 'agentnetwork':   return <AgentNetwork />;
    case 'bridge':         return <ClaudeBridge />;
    case 'india':          return <IndiaOverview />;
    case 'analyzer':       return <StockAnalyzer />;
    case 'screener':       return <StockScreener />;
    case 'indiajournal':   return <IndiaJournal />;
    case 'chartvision':    return <ChartVision />;
    case 'calendar':       return <EconomicCalendar />;
    case 'learningimport': return <LearningImport />;
    case 'tradeimport':    return <TradeImport />;
    case 'brokerconnect':  return <BrokerConnect />;
    case 'rules':          return <RuleBook />;
    case 'guide':          return <UserGuide />;
    default:               return <JarvisHome />;
  }
}

// ── Single nav card item
function NavItem({ item, active, onClick }) {
  const [hover, setHover] = useState(false);
  const on = active || hover;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '11px',
        margin: '2px 10px',
        padding: '10px 13px',
        borderRadius: '10px',
        background: active
          ? 'linear-gradient(90deg, rgba(0,212,255,0.14), rgba(0,212,255,0.04))'
          : hover
          ? 'rgba(255,255,255,0.05)'
          : 'rgba(255,255,255,0.025)',
        border: active
          ? '1px solid rgba(0,212,255,0.25)'
          : hover
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(255,255,255,0.04)',
        cursor: 'pointer',
        transition: 'all 0.15s',
        borderLeft: active ? '3px solid #00d4ff' : '3px solid transparent',
      }}
    >
      <span style={{
        fontSize: '17px',
        flexShrink: 0,
        width: '22px',
        textAlign: 'center',
        animation: active ? 'navIco 2s ease-in-out infinite' : 'none',
      }}>{item.emoji}</span>
      <span style={{
        fontSize: '13.5px',
        fontWeight: active ? '700' : '400',
        color: active ? '#00d4ff' : on ? '#e2e8f0' : 'rgba(255,255,255,0.6)',
        letterSpacing: '0.2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>{item.label}</span>
    </div>
  );
}

// ── Main V5Shell
export default function V5Shell() {
  const { state, dispatch } = useJarvisContext();
  const screen = state.activeScreen || 'v5home';
  const isMobile = useIsMobile();
  // Uses the EXISTING global sidebarOpen/TOGGLE_SIDEBAR already in
  // JarvisContext's reducer (SET_SCREEN already resets it to false on
  // every nav) instead of a separate local useState — this is what lets
  // BottomBar/InstallPWA/EnableNotifications, which all already read this
  // same context, hide themselves while the drawer is open too (see the
  // stacking-context note on the root div below for why that matters).
  const sidebarOpen = state.sidebarOpen;
  // Defensive: if a drawer was left open on mobile and the viewport then
  // grows past the breakpoint (resize, orientation change, DevTools),
  // don't leave stale "open" state around.
  React.useEffect(() => { if (!isMobile && sidebarOpen) dispatch({ type: A.TOGGLE_SIDEBAR }); }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = (id) => {
    dispatch({ type: A.SET_SCREEN, payload: id }); // already resets sidebarOpen to false
  };

  // Find current screen label for header
  const currentItem = NAV_GROUPS.flatMap(g => g.items).find(i => i.id === screen);
  const screenLabel = currentItem?.label || 'JARVIS AI';
  const screenEmoji = currentItem?.emoji || '◈';

  return (
    <>
      <style>{STYLES}</style>

      <div style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', flexDirection: 'column',
        background: '#07091a',
        color: '#e2e8f0',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        overflow: 'hidden',
      }}>

        {/* ════ HEADER ════════════════════════════════════════════════ */}
        <header style={{
          height: '66px', flexShrink: 0,
          background: 'rgba(5,7,18,0.98)',
          borderBottom: '1px solid rgba(0,212,255,0.1)',
          display: 'flex', alignItems: 'center',
          padding: '0 20px',
          gap: '14px',
          backdropFilter: 'blur(20px)',
          zIndex: 10,
        }}>
          {/* Hamburger — hidden by default (inline style), CSS forces it
              visible only at <=860px via .v5-hamburger !important */}
          <button
            className="v5-hamburger"
            onClick={() => dispatch({ type: A.TOGGLE_SIDEBAR })}
            aria-label="Toggle menu"
            style={{
              display: 'none', flexShrink: 0,
              width: '38px', height: '38px', borderRadius: '10px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.09)',
              color: '#00d4ff', fontSize: '18px', cursor: 'pointer',
              alignItems: 'center', justifyContent: 'center',
            }}>
            {sidebarOpen ? '✕' : '☰'}
          </button>
          <HeaderHud size={50} />
          <div className="v5-header-title" style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '21px', fontWeight: '900', color: '#00d4ff',
              letterSpacing: '2px', fontFamily: 'monospace', lineHeight: 1.15,
              textShadow: '0 0 16px rgba(0,212,255,0.55)',
              animation: 'shG 3s ease-in-out infinite',
            }}>JARVIS AI</div>
            <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.38)', letterSpacing: '0.4px',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Your AI Trading &amp; Teaching Assistant
            </div>
          </div>
          {/* Header right icons — on mobile .v5-header-title above is
              display:none, so this group gets margin-left:auto (CSS,
              same media query) to stay flush-right instead of leaving a
              gap where the title used to be. */}
          <div className="v5-header-icons" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {/* WiFi QR code — connects ANOTHER device to this PC's local
                server. Pointless (and hidden via .v5-wifi-btn) when
                you're already viewing JARVIS on the phone itself. */}
            <span className="v5-wifi-btn"><WifiConnect /></span>
            {[
              { icon: '🔍', tip: 'Search' },
              { icon: '🔔', tip: 'Alerts', dot: true },
            ].map(btn => (
              <button key={btn.tip} title={btn.tip} style={{
                width: '38px', height: '38px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.09)',
                cursor: 'pointer', fontSize: '17px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(0,212,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.05)'}>
                {btn.icon}
                {btn.dot && <div style={{
                  position: 'absolute', top: '7px', right: '7px',
                  width: '7px', height: '7px', borderRadius: '50%',
                  background: '#3b82f6', border: '1.5px solid #07091a',
                  animation: 'dotB 2s ease-in-out infinite',
                }}/>}
              </button>
            ))}
            {/* Settings */}
            <button onClick={() => nav('settings')} title="Settings" style={{
              width: '38px', height: '38px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.09)',
              cursor: 'pointer', fontSize: '17px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(0,212,255,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.05)'}>
              ⚙️
            </button>
          </div>
        </header>

        {/* ════ BODY ═══════════════════════════════════════════════════ */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* Backdrop — mobile only (CSS), closes the drawer on tap */}
          <div
            className={`v5-overlay${sidebarOpen ? ' open' : ''}`}
            onClick={() => sidebarOpen && dispatch({ type: A.TOGGLE_SIDEBAR })}
          />

          {/* ── SIDEBAR ──────────────────────────────────────────────── */}
          <aside className={`v5-sidebar${sidebarOpen ? ' open' : ''}`} style={{
            width: '252px', flexShrink: 0,
            background: 'rgba(4,6,16,0.99)',
            borderRight: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}>
            <nav style={{ flex: 1, padding: '10px 0 8px' }}>
              {NAV_GROUPS.map(group => (
                <div key={group.group}>
                  {/* Group label */}
                  <div style={{
                    fontSize: '10px', fontWeight: '700',
                    letterSpacing: '2px', color: 'rgba(255,255,255,0.2)',
                    padding: '14px 22px 5px',
                    fontFamily: 'monospace',
                  }}>{group.group}</div>

                  {/* Items */}
                  {group.items.map(item => (
                    <NavItem
                      key={item.id}
                      item={item}
                      active={screen === item.id}
                      onClick={() => nav(item.id)}
                    />
                  ))}
                </div>
              ))}
            </nav>

            {/* User profile at bottom */}
            <div style={{
              padding: '12px 10px',
              borderTop: '1px solid rgba(255,255,255,0.06)',
              margin: '0',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer', transition: 'background 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.03)'}>
                <div style={{
                  width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #374151, #1f2937)',
                  border: '1.5px solid rgba(255,255,255,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', fontWeight: '800', color: '#9ca3af',
                }}>N</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#e2e8f0', lineHeight: 1.3 }}>Nagesh</div>
                  <div style={{ fontSize: '11px', color: '#00d4ff', fontWeight: '600' }}>Pro Plan</div>
                </div>
                <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '16px' }}>›</span>
              </div>
            </div>
          </aside>

          {/* ── CONTENT ──────────────────────────────────────────────── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {/* Content header breadcrumb */}
            <div style={{
              height: '44px', flexShrink: 0,
              padding: '0 22px',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              display: 'flex', alignItems: 'center', gap: '10px',
              background: 'rgba(5,7,18,0.7)',
            }}>
              <span style={{ fontSize: '16px' }}>{screenEmoji}</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#e2e8f0' }}>{screenLabel}</span>
              <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.25)' }}>· JARVIS AI</span>
            </div>

            {/* Scrollable screen content */}
            <main className="v5-content" style={{
              flex: 1, overflowY: 'auto', overflowX: 'hidden',
              // Only reserve space for the fixed BottomBar on screens
              // that actually have one (see the BottomBar render below).
              paddingBottom: screen === 'v5chat' ? 0 : '80px',
              minHeight: 0,
            }}>
              <V5Screen id={screen} />
            </main>
          </div>
        </div>
      </div>

      {/* BottomBar fixed above all — except on the Chat screen itself,
          which already has its own full input row (ChatInput.jsx: text,
          attach, mic, send, quick-prompts). Both fixed to the bottom at
          once meant two overlapping input bars stacked on top of each
          other there — confirmed via a real mobile screenshot, not
          theory. Every other screen still gets the quick "Ask JARVIS"
          shortcut.
          Also hidden while the mobile drawer is open: BottomBar is a
          SIBLING of the app-shell root div above (not nested inside it),
          so the sidebar's own z-index:2000 never actually outranks it —
          a child's z-index can't escape its parent's stacking context.
          Confirmed as a real, reproducible bug via Playwright: clicking
          "Settings" near the bottom of the open drawer kept hitting
          BottomBar's fixed bar underneath instead. Simplest correct fix
          is just not rendering it while the drawer covers that area,
          rather than fighting the stacking context. */}
      {screen !== 'v5chat' && !sidebarOpen && <BottomBar />}
    </>
  );
}
